import { Injectable, signal } from '@angular/core';
import { ApiException } from '../services/api.clients.generated';
import { NotificationAction, NotificationItem, NotificationSeverity } from './notification.model';

interface NotificationOptions {
  title?: string;
  durationMs?: number;
  action?: NotificationAction;
}

const DEFAULT_DURATION_BY_SEVERITY: Record<NotificationSeverity, number> = {
  success: 4000,
  error: 7000,
  warning: 6000,
  info: 5000
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly notifications = signal<NotificationItem[]>([]);
  private nextId = 1;

  success(message: string, options?: NotificationOptions) {
    this.push('success', message, options);
  }

  error(message: string, options?: NotificationOptions) {
    this.push('error', message, options);
  }

  warning(message: string, options?: NotificationOptions) {
    this.push('warning', message, options);
  }

  info(message: string, options?: NotificationOptions) {
    this.push('info', message, options);
  }

  dismiss(id: number) {
    this.notifications.update(items => items.filter(item => item.id !== id));
  }

  errorMessage(error: unknown, fallbackMessage = 'Une erreur est survenue.'): string {
    const extracted = this.extractErrorMessage(error);
    return extracted || fallbackMessage;
  }

  private push(severity: NotificationSeverity, message: string, options?: NotificationOptions) {
    const item: NotificationItem = {
      id: this.nextId++,
      severity,
      message,
      title: options?.title,
      durationMs: options?.durationMs ?? DEFAULT_DURATION_BY_SEVERITY[severity],
      action: options?.action
    };

    this.notifications.update(items => [...items, item]);

    if (item.durationMs > 0) {
      window.setTimeout(() => this.dismiss(item.id), item.durationMs);
    }
  }

  private extractErrorMessage(error: unknown): string | null {
    if (!error) {
      return null;
    }

    if (typeof error === 'string') {
      return error.trim() || null;
    }

    if (error instanceof ApiException) {
      return this.extractErrorMessage(error.result)
        ?? this.extractErrorMessage(this.parseApiResponse(error.response))
        ?? error.message?.trim()
        ?? null;
    }

    if (error instanceof Error) {
      return error.message?.trim() || null;
    }

    if (typeof error === 'object') {
      const candidate = error as Record<string, unknown>;
      const keysToCheck = ['message', 'error', 'detail', 'title', 'userMessage'];
      for (const key of keysToCheck) {
        const nested = this.extractErrorMessage(candidate[key]);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  }

  private parseApiResponse(response: string | undefined): unknown {
    if (!response) {
      return null;
    }

    try {
      return JSON.parse(response);
    } catch {
      return response;
    }
  }
}
